/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
'use strict';

const GatewayAPI = require('./GatewayAPI');
const {
	// Graph requests
	// Machine requests
	AddInboxMessageWebSocketRequest,
	CreateStreamSheetWebSocketRequest,
	DeleteMachineWebSocketRequest,
	DeleteStreamSheetWebSocketRequest,
	GetMachinesWebSocketRequest,
	GetCellRawValueWebSocketRequest,
	LoadMachineWebSocketRequest,
	LoadSubscribeMachineWebSocketRequest,
	OpenMachineWebSocketRequest,
	PauseMachineWebSocketRequest,
	RenameMachineWebSocketRequest,
	UpdateMachineImageWebSocketRequest,
	UpdateMachineTitleImageWebSocketRequest,
	UpdateStreamSheetStreamsWebSocketRequest,
	SaveMachineAsWebSocketRequest,
	ConfirmLoadSubscribeMachineRequest,
	ConfirmProcessedMachineStepRequest,
	StartMachineWebSocketRequest,
	StartMachinesWebSocketRequest,
	StepMachineWebSocketRequest,
	StopMachineWebSocketRequest,
	StopMachinesWebSocketRequest,
	SubscribeMachineWebSocketRequest,
	RedoWebSocketRequest,
	UndoWebSocketRequest,
	UnsubscribeMachineWebSocketRequest,
	UpdateMachineSettingsWebSocketRequest,
	// General requests
	CommandWebSocketRequest,
	MachineActionWebSocketRequest,
	UserLoginSocketRequest,
	UserLogoutSocketRequest,
	UnloadMachineWebSocketRequest,
	SetNamedCellsWebSocketRequest,
} = require('../../requests/sockets/WebSocketRequests');

const deletePendingRequest = (requestId, requests) => {
	const request = requests.get(requestId);
	if (request) {
		clearTimeout(request.timeoutId);
		requests.delete(requestId);
	}
	return request;
};
const timeoutHandler = (requestId, requests) => {
	const { reject } = deletePendingRequest(requestId, requests);
	reject({
		message: 'Timeout',
		requestId
	});
};

module.exports = class WebSocketGatewayAPI extends GatewayAPI {
	constructor(ws, logger) {
		super(logger);
		this._ws = ws;
		this._requests = new Map();
		// TODO: make timeout configurable
		// request timeout in ms:
		this._timeout = 20000;
	}

	/**
	 * ******************************************************************************************
	 * High Level API: Web Socket API
	 * ******************************************************************************************
	 */

	login(credentials) {
		return this.sendRequest(
			new UserLoginSocketRequest(this._ws, credentials)
		);
	}
		
	logout(id) {
		return this.sendRequest(new UserLogoutSocketRequest(this._ws, id));
	}

	getMachines() {
		return this.sendRequest(new GetMachinesWebSocketRequest(this._ws));
	}

	deleteMachine(machineId, scope) {
		return this.sendRequest(
			new DeleteMachineWebSocketRequest(this._ws, machineId, scope)
		);
	}

	loadMachine(machineId, settings, scope) {
		return this.sendRequest(
			new LoadMachineWebSocketRequest(this._ws, machineId, settings, scope),
			180000
		);
	}

	unloadMachine(machineId) {
		return this.sendRequest(
			new UnloadMachineWebSocketRequest(this._ws, machineId)
			);
	}

	loadSubscribeMachine(machineId, settings, scope) {
		return this.sendRequest(
			new LoadSubscribeMachineWebSocketRequest(this._ws, machineId, settings, scope),
			180000
		);
	}

	openMachine(machineId) {
		return this.sendRequest(
			new OpenMachineWebSocketRequest(this._ws, machineId)
			);
	}

	pauseMachine(machineId) {
		return this.sendRequest(
			new PauseMachineWebSocketRequest(this._ws, machineId)
		);
	}

	updateMachineImage(machineId, previewImage) {
		return this.sendRequest(
			new UpdateMachineImageWebSocketRequest(
				this._ws,
				machineId,
				previewImage
			)
		);
	}

	updateMachineTitleImage(machineId, titleImage) {
		return this.sendRequest(
			new UpdateMachineTitleImageWebSocketRequest(
				this._ws,
				machineId,
				titleImage
			)
		);
	}

	renameMachine(machineId, newName) {
		return this.sendRequest(
			new RenameMachineWebSocketRequest(this._ws, machineId, newName)
		);
	}

	updateNamedCells(machineId, streamsheetId, namedCells) {
		return this.sendRequest(
			new SetNamedCellsWebSocketRequest(
				this._ws,
				machineId,
				streamsheetId,
				namedCells
			)
		);
	}

	addInboxMessage(machineId, streamsheetId, message, metadata) {
		return this.sendRequest(
			new AddInboxMessageWebSocketRequest(
				this._ws,
				machineId,
				streamsheetId,
				message,
				metadata
			)
		);
	}

	createStreamSheet(machineId, activeItemId, settings, scope) {
		return this.sendRequest(
			new CreateStreamSheetWebSocketRequest(
				this._ws,
				machineId,
				activeItemId,
				settings,
				scope
			)
		);
	}

	deleteStreamSheet(machineId, streamsheetId, scope) {
		return this.sendRequest(
			new DeleteStreamSheetWebSocketRequest(
				this._ws,
				machineId,
				streamsheetId,
				scope
			)
		);
	}

	updateStreamSheetStreams(machineId, streamsheetId, streams) {
		return this.sendRequest(
			new UpdateStreamSheetStreamsWebSocketRequest(
				this._ws,
				machineId,
				streamsheetId,
				streams
			)
		);
	}

	confirmLoadSubscribeMachine(machineId) {
		return this.sendRequest(
			new ConfirmLoadSubscribeMachineRequest(this._ws, machineId)
		);
	}

	confirmProcessedMachineStep(machineId) {
		return this.sendRequest(
			new ConfirmProcessedMachineStepRequest(this._ws, machineId)
		);
	}

	startMachine(machineId) {
		return this.sendRequest(
			new StartMachineWebSocketRequest(this._ws, machineId)
		);
	}

	startMachines() {
		return this.sendRequest(new StartMachinesWebSocketRequest(this._ws));
	}

	stepMachine(machineId) {
		return this.sendRequest(
			new StepMachineWebSocketRequest(this._ws, machineId)
		);
	}

	stopMachine(machineId) {
		return this.sendRequest(
			new StopMachineWebSocketRequest(this._ws, machineId)
		);
	}

	stopMachines() {
		return this.sendRequest(new StopMachinesWebSocketRequest(this._ws));
	}

	subscribeMachine(machineId) {
		return this.sendRequest(
			new SubscribeMachineWebSocketRequest(this._ws, machineId),
			180000
		);
	}

	redo(machineId) {
		return this.sendRequest(new RedoWebSocketRequest(this._ws, machineId));
	}

	undo(machineId) {
		return this.sendRequest(new UndoWebSocketRequest(this._ws, machineId));
	}

	unsubscribeMachine(machineId) {
		return this.sendRequest(
			new UnsubscribeMachineWebSocketRequest(this._ws, machineId)
		);
	}

	updateMachineSettings(machineId, settings) {
		return this.sendRequest(
			new UpdateMachineSettingsWebSocketRequest(
				this._ws,
				machineId,
				settings
			)
		);
	}

	saveMachineAs(originalMachineId, newName) {
		return this.sendRequest(
			new SaveMachineAsWebSocketRequest(
				this._ws,
				originalMachineId,
				newName
			)
		);
	}

	sendCommand(machineId, command) {
		return this.sendRequest(
			new CommandWebSocketRequest(
				this._ws,
				machineId,
				command,
			)
		);
	}
	sendMachineAction(machineId, action) {
		return this.sendRequest(
			new MachineActionWebSocketRequest(
				this._ws,
				machineId,
				action
			)
		);
	}
	getCellRawValue(machineId, streamsheetId, reference) {
		return this.sendRequest(new GetCellRawValueWebSocketRequest(this._ws, machineId, streamsheetId, reference));
	}


	/**
	 * ******************************************************************************************
	 * Low Level API
	 * ******************************************************************************************
	 */

	sendRequest(request, timeout = this._timeout) {
		/* eslint-disable */
		this.logger.debug('Sending request to Gateway', request);
		return new Promise((resolve, reject) => {
			const timeoutId = setTimeout(
				() => timeoutHandler(request.id, this._requests),
				timeout
			);
			this._requests.set(request.id, {
				resolve,
				reject,
				timeoutId,
				request
			});
			return request.send().catch((error) => {
				this.logger.error(
					'Sending request to Gateway',
					request._getConfig()
				);
				this.logger.error(
					`Error while communicating with Gateway while executing request '${
						request.constructor.name
					}'`,
					error
				);
				throw error;
			});
		});
		/* eslint-enable */
	}

	_handleSocketMessage(message) {
		/* eslint-disable */
		const request = deletePendingRequest(message.requestId, this._requests);
		if (request) {
			if (message.type === 'response') {
				this.logger.debug('Got response from Gateway', message);
				return request.resolve(message);
			} else {
				return request.reject(message);
			}
		}
		return null;
		/* eslint-enable */
	}
};
